import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BarcodeScannerContext } from './BarcodeScannerContext';
import { NativeBarcodeEventDetail, ScanHandler } from '../state';

export const BarcodeScannerProvider = ({ children }: { children: ReactNode }) => {
    const [scannerEnabled, setScannerEnabled] = useState(true);
    const [activeTargetId, setActiveTargetIdState] = useState<string | null>(null);

    const activeTargetIdRef = useRef<string | null>(null);
    const handlersRef = useRef(new Map<string, ScanHandler>());

    const setActiveTargetId = useCallback((id: string | null) => {
        activeTargetIdRef.current = id;
        setActiveTargetIdState(id);
    }, []);

    const registerTarget = useCallback((id: string, handler: ScanHandler) => {
        handlersRef.current.set(id, handler);

        return () => {
            handlersRef.current.delete(id);

            if (activeTargetIdRef.current === id) {
                activeTargetIdRef.current = null;
                setActiveTargetIdState(null);
            }
        };
    }, []);

    useEffect(() => {
        const handleNativeBarcode = (event: Event) => {
            if (!scannerEnabled) return;

            const customEvent = event as CustomEvent<NativeBarcodeEventDetail>;
            const barcode = customEvent.detail?.value?.trim();

            if (!barcode) return;

            const targetId = activeTargetIdRef.current;
            if (!targetId) return;

            const handler = handlersRef.current.get(targetId);
            if (!handler) return;

            void handler(barcode);
        };

        window.addEventListener('aura-barcode-scanned', handleNativeBarcode);

        return () => {
            window.removeEventListener('aura-barcode-scanned', handleNativeBarcode);
        };
    }, [scannerEnabled]);

    const clearActiveTargetId = useCallback((id: string) => {
        if (activeTargetIdRef.current !== id) return;

        activeTargetIdRef.current = null;
        setActiveTargetIdState(null);
    }, []);

    const value = useMemo(
        () => ({
            scannerEnabled,
            setScannerEnabled,
            activeTargetId,
            setActiveTargetId,
            registerTarget,
            clearActiveTargetId,
        }),
        [scannerEnabled, activeTargetId, setActiveTargetId, registerTarget, clearActiveTargetId],
    );

    return <BarcodeScannerContext.Provider value={value}>{children}</BarcodeScannerContext.Provider>;
};
